import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Clock, AlertCircle, RotateCcw, Timer, Shield, Inbox,
  AlertTriangle, Hourglass, ChevronDown, ChevronUp,
} from 'lucide-react';

// Core KPIs always visible
const CORE_CARDS = [
  { key: 'total_pending', label: 'Total Pending', icon: Inbox, color: '#1e40af', testid: 'kpi-total-pending' },
  { key: 'awaiting_score', label: 'Awaiting Score', icon: Timer, color: '#7c3aed', testid: 'kpi-awaiting-score' },
  { key: 'pending_review', label: 'Pending Review', icon: AlertCircle, color: '#d97706', testid: 'kpi-pending-review' },
  { key: 'returned', label: 'Returned', icon: RotateCcw, color: '#2563eb', testid: 'kpi-returned' },
];

// Toggle-revealed KPIs (details / governance).
// Chairman feedback (Feb 2026): removed Approved Today / Rejected Today.
// Segmented Median Time Taken for Review (New vs Existing) lives here — the
// right-rail duplicate has been dropped.
const DETAIL_CARDS = [
  { key: 'median_ttr_new_label', label: 'Median Time Taken for Review (New)', icon: Clock, color: '#475569', testid: 'kpi-median-ttr-new', is_text: true, subtitle: 'New Farmer · 30d' },
  { key: 'median_ttr_existing_label', label: 'Median Time Taken for Review (Existing)', icon: Clock, color: '#475569', testid: 'kpi-median-ttr-existing', is_text: true, subtitle: 'Existing Farmer · 30d' },
  { key: 'sla_breaches', label: 'Cases Breaching SLA', icon: AlertTriangle, color: '#dc2626', testid: 'kpi-sla-breaches', alert_if_gt: 0, subtitle: '>3 days pending' },
  { key: 'oldest_pending_label', label: 'Oldest Pending', icon: Hourglass, color: '#b45309', testid: 'kpi-oldest-pending', is_text: true },
  { key: 'override_count_30d', label: 'Overrides (30D)', icon: Shield, color: '#b45309', testid: 'kpi-override-count' },
];

const KpiCard = ({ c, data }) => {
  const Icon = c.icon;
  const value = data?.[c.key] ?? (c.is_text ? 'N/A' : 0);
  const isAlert = c.alert_if_gt != null && Number(value) > c.alert_if_gt;
  const cardStyle = isAlert ? { borderColor: '#fca5a5', background: '#fef2f2' } : {};
  return (
    <Card data-testid={c.testid} className="border hover:shadow-sm transition-shadow relative overflow-hidden" style={cardStyle}>
      {isAlert && <div className="absolute top-0 left-0 bottom-0 w-1" style={{ background: '#dc2626' }} />}
      <CardContent className="p-3">
        <div className="flex items-start justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 leading-tight">
            {c.label}
          </div>
          <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: isAlert ? '#dc2626' : c.color }} />
        </div>
        <div className="text-xl font-bold font-mono tabular-nums leading-tight" style={{ color: isAlert ? '#dc2626' : c.color }}>
          {c.is_text ? (value || 'N/A') : value}
        </div>
        {c.subtitle && <div className="text-[9px] text-gray-400 mt-0.5">{c.subtitle}</div>}
      </CardContent>
    </Card>
  );
};

const STORAGE_KEY = 'officer.inbox.metrics_expanded';

const InboxKpiStrip = ({ data, loading }) => {
  const [expanded, setExpanded] = useState(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    return stored === null ? true : stored === 'true';
  });
  useEffect(() => { localStorage.setItem(STORAGE_KEY, String(expanded)); }, [expanded]);

  if (loading && !data) {
    return (
      <div className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          {CORE_CARDS.map(c => (
            <Card key={c.key} className="animate-pulse"><CardContent className="p-3.5 h-[88px]" /></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6" data-testid="inbox-kpi-strip">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        {CORE_CARDS.map(c => <KpiCard key={c.key} c={c} data={data} />)}
      </div>

      <div className="flex items-center gap-2 my-3">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-[11px] text-gray-600"
          onClick={() => setExpanded(v => !v)}
          data-testid="kpi-toggle-btn"
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
          {expanded ? 'Collapse details' : 'View details'}
          <span className="ml-1.5 text-gray-400">({DETAIL_CARDS.length} metrics)</span>
        </Button>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      {expanded && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2.5" data-testid="inbox-kpi-details">
          {DETAIL_CARDS.map(c => <KpiCard key={c.key} c={c} data={data} />)}
        </div>
      )}
    </div>
  );
};

export default InboxKpiStrip;
